/**
 * api/designStudio.js — Design Studio (Fase 2 · Bloco 1).
 *
 * GET  /projects/{id}/design-studio            → telas atuais (maior versão)
 * POST /projects/{id}/design-studio/generate   → gera telas via agente real (Claude)
 * PUT  /projects/{id}/design-studio/{screenId} → edita (promove a v2)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

const key = (projectId) => ['design-studio', projectId];

export function useDesignStudio(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: key(projectId),
    queryFn:  () => api.get(`/projects/${projectId}/design-studio`),
    enabled:  Boolean(projectId),
  });
}

/** useGenerateMissing — gera SÓ as telas faltantes (lacunas) e anexa. */
export function useGenerateMissing(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'Gerando as telas que faltam…',
            aiSublabel: 'Cobrindo as lacunas do escopo sem refazer as telas existentes.' },
    mutationFn: (body) => api.post(`/projects/${projectId}/design-studio/generate-missing`, body),
    onSuccess: (data) => {
      qc.setQueryData(key(projectId), data);
      qc.invalidateQueries({ queryKey: ['design-adherence', projectId] });
    },
  });
}

/**
 * useApproveDesignScreens — aprova (ou reabre) telas. Corpo: `{ all: true }` ou
 * `{ screen_ids: [...], approve: true|false }`. A resposta traz o conjunto atualizado e as
 * telas recusadas (layout cortado), com o motivo.
 */
export function useApproveDesignScreens(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/design-studio/approve`, body),
    onSuccess: (data) => {
      qc.setQueryData(key(projectId), (antes) => ({ ...(antes || {}), screens: data.screens }));
    },
  });
}

/**
 * useGenerateWebLayouts — gera o layout WEB das telas que só têm o de celular, UM PACOTE por
 * chamada. A resposta diz `generated`, `remaining` e `hasMore`; quem chama repete enquanto
 * houver mais.
 */
export function useGenerateWebLayouts(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/design-studio/generate-web-layouts`, body || {}),
    onSuccess: (data) => {
      qc.setQueryData(key(projectId), (antes) => ({ ...(antes || {}), screens: data.screens }));
    },
  });
}

/** useAdherence — relatório salvo de aderência (escopo × telas). */
export function useAdherence(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['design-adherence', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/design-studio/adherence`),
    enabled:  Boolean(projectId),
  });
}

/** useComputeAdherence — calcula a aderência de jornada (escopo × telas) via IA. */
export function useComputeAdherence(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'Validando aderência ao escopo…',
            aiSublabel: 'Comparando o que você pediu com as telas geradas (jornada).' },
    mutationFn: () => api.post(`/projects/${projectId}/design-studio/adherence`, {}),
    onSuccess:  (data) => qc.setQueryData(['design-adherence', projectId], data),
  });
}

/**
 * useDesignProgress — progresso da geração de telas (X/N · faltam Y).
 * Faz polling a cada 2s ENQUANTO `active` (fase de geração das telas), para a
 * animação de IA mostrar avanço e não dar impressão de travado.
 * @param {string|null} projectId
 * @param {boolean} active
 */
export function useDesignProgress(projectId, active) {
  const api = useApi();
  return useQuery({
    queryKey: ['design-progress', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/design-studio/progress`),
    enabled:  Boolean(projectId) && Boolean(active),
    refetchInterval: active ? 2000 : false,
    staleTime: 0,
  });
}

export function useGenerateDesignStudio(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está gerando as telas do protótipo…', aiSublabel: 'Desenhando as telas a partir do escopo de negócio aprovado.' },
    mutationFn: () => api.post(`/projects/${projectId}/design-studio/generate`, {}),
    onSuccess:  () => qc.invalidateQueries({ queryKey: key(projectId) }),
  });
}

/**
 * useGenerateDesignBatch — DS-v2: gera o PRÓXIMO pacote (~size telas), anexa e retorna
 * { newScreens, totalScreens, hasMore }. body: { size, reset, note }.
 */
export function useGenerateDesignBatch(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está gerando o próximo pacote de telas…', aiSublabel: 'Telas navegáveis para validação visual do negócio × produto.' },
    mutationFn: (body) => api.post(`/projects/${projectId}/design-studio/generate-batch`, body || {}),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: key(projectId) });
      qc.invalidateQueries({ queryKey: ['design-adherence', projectId] });
    },
  });
}

/** useScopeAudit — lê a auditoria agnóstica do escopo (cacheada). */
export function useScopeAudit(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['scope-audit', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/design-studio/scope-audit`),
    enabled:  Boolean(projectId),
  });
}

/** useComputeScopeAudit — roda a auditoria agnóstica (IA olhos frescos, sem RAG). */
export function useComputeScopeAudit(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'Auditando o escopo com olhos frescos…', aiSublabel: 'Comparando os documentos do cliente com as telas geradas (sem usar memória/contexto salvo).' },
    mutationFn: () => api.post(`/projects/${projectId}/design-studio/scope-audit`, {}),
    onSuccess:  (data) => qc.setQueryData(['scope-audit', projectId], data),
  });
}

/** useSaveClarifications — salva respostas às dúvidas + exclusões de escopo. */
export function useSaveClarifications(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/design-studio/scope-clarifications`, body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['scope-audit', projectId] }),
  });
}

/** useReorderDesign — reordena as telas pela jornada do produto (escopo), sem regerar. */
export function useReorderDesign(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'Ordenando as telas pela jornada do produto…', aiSublabel: 'Alinhando a sequência das telas ao escopo.' },
    mutationFn: () => api.post(`/projects/${projectId}/design-studio/reorder`, {}),
    onSuccess:  () => qc.invalidateQueries({ queryKey: key(projectId) }),
  });
}

/** useDesignPrototype — protótipo HTML navegável (arquivo único) do projeto. */
export function useDesignPrototype(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['design-prototype', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/design-studio/prototype`),
    enabled:  Boolean(projectId),
  });
}

/** useGenerateDesignPrototype — gera/regenera o protótipo navegável via Claude. */
export function useGenerateDesignPrototype(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    mutationFn: () => api.post(`/projects/${projectId}/design-studio/prototype`, {}),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['design-prototype', projectId] }),
  });
}

/** useFrontendStructure — estrutura de arquivos front-end persistida do projeto. */
export function useFrontendStructure(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['frontend-structure', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/design-studio/frontend-structure`),
    enabled:  Boolean(projectId),
  });
}

/** useGenerateFrontendStructure — gera (e persiste) a árvore de arquivos front-end. */
export function useGenerateFrontendStructure(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    mutationFn: () => api.post(`/projects/${projectId}/design-studio/frontend-structure`, {}),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['frontend-structure', projectId] }),
  });
}

/**
 * useScopeProfile — classifica o escopo: precisa de telas (Design Studio) ou é
 * só integração? Query cacheada (roda 1x por sessão).
 */
export function useScopeProfile(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['scope-profile', projectId],
    queryFn:  () => api.post(`/projects/${projectId}/scope-profile`, {}),
    enabled:  Boolean(projectId),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useImpactAnalysis(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['impact-analysis', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/design-studio/impact-analysis`),
    enabled:  Boolean(projectId),
  });
}

export function useRunImpactAnalysis(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está analisando o impacto…', aiSublabel: 'Avaliando o efeito das mudanças de design no projeto.' },
    mutationFn: () => api.post(`/projects/${projectId}/design-studio/impact-analysis`, {}),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['impact-analysis', projectId] }),
  });
}
